<template>
  <div class="level-container">
    <!-- 添加开始游戏按钮 -->
    <div class="start-overlay" v-if="!gameStarted">
      <div class="start-content">
        <h2>🏃‍♂️ 打字竞速</h2>
        <p class="start-description">
          和乌龟赛跑吧！快速准确地输入目标键位，让小兔子跑得更快。
          <br />注意：乌龟会一直前进，别让它超过你哦！
        </p>
        <!-- 添加难度选择 -->
        <div class="difficulty-selection">
          <h3>选择难度</h3>
          <div class="difficulty-options">
            <button
              class="difficulty-btn"
              :class="{ active: selectedDifficulty === 'easy' }"
              @click="selectDifficulty('easy')"
            >
              <span class="difficulty-icon">🐣</span>
              <span class="difficulty-name">简单</span>
              <span class="difficulty-desc">适合初学者</span>
            </button>
            <button
              class="difficulty-btn"
              :class="{ active: selectedDifficulty === 'medium' }"
              @click="selectDifficulty('medium')"
            >
              <span class="difficulty-icon">🐥</span>
              <span class="difficulty-name">中等</span>
              <span class="difficulty-desc">有一定挑战</span>
            </button>
            <button
              class="difficulty-btn"
              :class="{ active: selectedDifficulty === 'hard' }"
              @click="selectDifficulty('hard')"
            >
              <span class="difficulty-icon">🦅</span>
              <span class="difficulty-name">困难</span>
              <span class="difficulty-desc">高速挑战</span>
            </button>
          </div>
        </div>
        <div class="start-buttons">
          <button class="start-btn" @click="startGame">
            <span class="button-icon">🎮</span>
            开始比赛
          </button>
          <button class="back-btn" @click="backToMap">
            <span class="button-icon">🗺️</span>
            返回
          </button>
        </div>
      </div>
    </div>

    <div class="game-content" v-show="gameStarted">
      <div class="race-track">
        <div class="track-background">
          <div class="track-line"></div>
          <div class="distance-markers">
            <span>起点</span>
            <span>25%</span>
            <span>50%</span>
            <span>75%</span>
            <span>100%</span>
          </div>
        </div>
        <div class="rabbit" :style="{ left: `${rabbitPosition}%` }">
          <span class="character">🐰</span>
          <div class="shadow"></div>
        </div>
        <div class="turtle" :style="{ left: `${turtlePosition}%` }">
          <span class="character turtle-char">🐢</span>
          <div class="shadow"></div>
        </div>
        <div class="finish-line">
          <span class="flag">🏁</span>
          <div class="finish-text">终点线</div>
        </div>
      </div>

      <div class="game-display" :class="{ correct: isCorrect, wrong: isWrong }">
        <div class="target-letters">
          <span
            :class="{
              current: true,
              space: currentLetter === ' ',
            }"
            >{{ currentLetter }}</span
          >
        </div>
        <div class="letter-instruction">
          {{ currentLetter === " " ? "请按空格键" : `请输入${currentLetter}` }}
        </div>
      </div>

      <div class="progress-container">
        <div class="progress-bar">
          <div class="progress" :style="{ width: `${progress}%` }">
            <div class="progress-glow"></div>
          </div>
        </div>
        <div class="progress-stats">
          <div class="stat">
            <span class="stat-label">🐰 兔子位置</span>
            <span class="stat-value">{{ Math.floor(rabbitPosition) }}%</span>
          </div>
          <div class="stat">
            <span class="stat-label">🐢 乌龟位置</span>
            <span class="stat-value">{{ Math.floor(turtlePosition) }}%</span>
          </div>
        </div>
        <!-- 添加难度显示 -->
        <div class="difficulty-display">
          <span class="difficulty-label">当前难度:</span>
          <span
            class="difficulty-value"
            :class="'difficulty-' + selectedDifficulty"
          >
            {{
              selectedDifficulty === "easy"
                ? "🐣 简单"
                : selectedDifficulty === "medium"
                ? "🐥 中等"
                : "🦅 困难"
            }}
          </span>
        </div>
        <!-- 添加控制按钮 -->
        <div class="control-buttons">
          <button class="control-btn restart" @click="restartCurrentGame">
            <span class="button-icon">🔄</span>
            重新开始
          </button>
          <button class="control-btn exit" @click="exitGame">
            <span class="button-icon">🚪</span>
            退出
          </button>
        </div>
      </div>
    </div>

    <!-- 在现有模板末尾添加结果报告弹窗 -->
    <div class="report-modal" v-if="showReport">
      <div class="report-content">
        <div class="report-header">
          <h2>{{ rabbitPosition >= 100 ? "🎉 胜利！" : "💪 继续加油！" }}</h2>
          <div class="close-button" @click="closeReport">×</div>
        </div>

        <div class="report-body">
          <div class="report-stats">
            <div class="stat-item">
              <div class="stat-icon">⏱️</div>
              <div class="stat-info">
                <div class="stat-label">用时</div>
                <div class="stat-value">{{ formatTime(totalTime) }}</div>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-icon">✨</div>
              <div class="stat-info">
                <div class="stat-label">正确率</div>
                <div class="stat-value">{{ accuracy }}%</div>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-icon">🐰</div>
              <div class="stat-info">
                <div class="stat-label">兔子位置</div>
                <div class="stat-value">{{ Math.floor(rabbitPosition) }}%</div>
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-icon">🐢</div>
              <div class="stat-info">
                <div class="stat-label">乌龟位置</div>
                <div class="stat-value">{{ Math.floor(turtlePosition) }}%</div>
              </div>
            </div>
          </div>

          <div class="report-message">
            <img :src="resultImage" :alt="resultMessage" />
            <p class="message-text">{{ resultMessage }}</p>
            <!-- 添加难度相关的鼓励信息 -->
            <p
              class="difficulty-tip"
              v-if="rabbitPosition >= 100 && selectedDifficulty !== 'hard'"
            >
              {{
                selectedDifficulty === "easy"
                  ? "👍 你已经掌握了基础，试试中等难度吧！"
                  : "🔥 你已经很熟练了，挑战一下困难模式吧！"
              }}
            </p>
            <p
              class="difficulty-tip"
              v-if="rabbitPosition < 100 && selectedDifficulty !== 'easy'"
            >
              {{
                selectedDifficulty === "hard"
                  ? "💡 困难模式需要更多练习，可以先尝试中等难度"
                  : "💪 多加练习，或者先从简单模式开始也可以"
              }}
            </p>
          </div>
        </div>

        <div class="report-footer">
          <button class="restart-btn" @click="restartGame">
            <span class="button-icon">🔄</span>
            再来一次
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TypingPlanetIntroRace",
  data() {
    return {
      currentLetter: "r",
      isCorrect: false,
      isWrong: false,
      progress: 0,
      rabbitPosition: 0,
      turtlePosition: 0,
      turtleSpeed: 0.2, // 乌龟每100ms移动的距离（百分比）
      rabbitSpeed: 2, // 兔子每次按对键移动的距离（百分比）
      gameInterval: null,
      gameStarted: false,
      gameFinished: false,
      bgm: null,
      isBgmPlaying: false,
      clickSound: new Audio(
        "https://wugecdn.steam.fun/typefun/music/correct.mp3"
      ),
      failSound: new Audio("https://wugecdn.steam.fun/typefun/music/fail.mp3"),
      showReport: false,
      startTime: null,
      totalTime: 0,
      correctCount: 0,
      totalCount: 0,
      accuracy: 100,
      resultMessage: "",
      resultImage: "",
      resultImages: {
        win: "https://wugecdn.steam.fun/typefun/images/excellent.png",
        lose: "https://wugecdn.steam.fun/typefun/images/keep-trying.png",
      },
      keyConfig: {
        keys: [],
        levelId: "",
        nextLevel: "",
      },
      // 添加难度相关数据
      selectedDifficulty: "medium", // 默认中等难度
      difficultySettings: {
        easy: {
          turtleSpeed: 0.15,
          rabbitSpeed: 2.5,
          description: "乌龟速度较慢，适合初学者",
        },
        medium: {
          turtleSpeed: 0.2,
          rabbitSpeed: 2,
          description: "标准速度，适合有一定基础的玩家",
        },
        hard: {
          turtleSpeed: 0.3,
          rabbitSpeed: 1.8,
          description: "乌龟速度较快，需要快速准确的输入",
        },
      },
    };
  },
  mounted() {
    window.addEventListener("keydown", this.handleKeyPress);
    // 初始化背景音乐
    this.bgm = new Audio(
      "https://wugecdn.steam.fun/typefun/music/race-bgm.mp3"
    );
    this.bgm.loop = true;
    this.bgm.volume = 0.3;

    this.getKeyConfigFromRoute();
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeyPress);
    if (this.gameInterval) {
      clearInterval(this.gameInterval);
    }
    // 停止背景音乐
    if (this.bgm) {
      this.bgm.pause();
      this.bgm = null;
    }
  },
  methods: {
    // 添加选择难度的方法
    selectDifficulty(difficulty) {
      this.selectedDifficulty = difficulty;
      // 更新乌龟和兔子的速度
      this.turtleSpeed = this.difficultySettings[difficulty].turtleSpeed;
      this.rabbitSpeed = this.difficultySettings[difficulty].rabbitSpeed;
    },
    getKeyConfigFromRoute() {
      if (this.$route.query.keys) {
        try {
          this.keyConfig.keys = this.$route.query.keys.split("");
        } catch (e) {
          console.error("键位参数格式错误:", e);
        }
      }

      if (this.$route.query.levelId) {
        this.levelId = this.$route.query.levelId;
      }

      if (this.$route.query.nextLevel) {
        this.keyConfig.nextLevel = this.$route.query.nextLevel;
      }

      this.generateNewLetter();
    },
    startGame() {
      this.gameStarted = true;
      this.rabbitPosition = 0;
      this.turtlePosition = 0;
      this.gameFinished = false;
      this.generateNewLetter();

      // 应用选择的难度设置
      const diffSettings = this.difficultySettings[this.selectedDifficulty];
      this.turtleSpeed = diffSettings.turtleSpeed;
      this.rabbitSpeed = diffSettings.rabbitSpeed;

      this.gameInterval = setInterval(() => {
        if (!this.gameFinished) {
          this.moveTurtle();
          this.checkGameStatus();
        }
      }, 100);
      // 开始播放背景音乐
      if (this.bgm && !this.isBgmPlaying) {
        this.bgm.play();
        this.isBgmPlaying = true;
      }
      this.startTime = new Date();
      this.correctCount = 0;
      this.totalCount = 0;
      this.accuracy = 100;
    },
    generateNewLetter() {
      if (this.keyConfig.keys.length > 0) {
        const randomIndex = Math.floor(
          Math.random() * this.keyConfig.keys.length
        );
        this.currentLetter = this.keyConfig.keys[randomIndex];
      } else {
        this.currentLetter = "r";
      }
    },
    handleKeyPress(event) {
      // 阻止空格键的默认行为（页面滚动）
      if (event.key === " ") {
        event.preventDefault();
      }

      if (this.gameFinished) return;

      const key = event.key.toLowerCase();
      this.totalCount++;

      // 修改按键判断逻辑，直接比较按下的键和当前字母
      const isCorrect = key === this.currentLetter;

      if (isCorrect) {
        this.isCorrect = true;
        this.isWrong = false;
        this.moveRabbit();
        this.generateNewLetter();
        this.clickSound.currentTime = 0;
        this.clickSound.play();
        setTimeout(() => {
          this.isCorrect = false;
        }, 200);
        this.correctCount++;
      } else if (this.keyConfig.keys.includes(key)) {
        // 只有当按下的键在配置的键位中时才显示错误
        this.isWrong = true;
        this.failSound.currentTime = 0;
        this.failSound.play();
        setTimeout(() => {
          this.isWrong = false;
        }, 200);
      }

      this.accuracy = Math.round((this.correctCount / this.totalCount) * 100);
    },
    moveRabbit() {
      this.rabbitPosition = Math.min(
        100,
        this.rabbitPosition + this.rabbitSpeed
      );
      this.updateProgress();
    },
    moveTurtle() {
      this.turtlePosition = Math.min(
        100,
        this.turtlePosition + this.turtleSpeed
      );
      this.updateProgress();
    },
    updateProgress() {
      this.progress = Math.max(this.rabbitPosition, this.turtlePosition);
    },
    checkGameStatus() {
      if (this.rabbitPosition >= 100 || this.turtlePosition >= 100) {
        this.gameFinished = true;
        clearInterval(this.gameInterval);
        // 停止背景音乐
        if (this.bgm) {
          this.bgm.pause();
          this.isBgmPlaying = false;
        }
        // 播放结果音效
        const resultSound = new Audio(
          this.rabbitPosition >= 100
            ? "https://wugecdn.steam.fun/typefun/music/win.mp3"
            : "https://wugecdn.steam.fun/typefun/music/lose.mp3"
        );
        resultSound.play();

        // 生成报告
        this.generateReport();
      }
    },
    backToMap() {
      // 停止背景音乐
      if (this.bgm) {
        this.bgm.pause();
        this.isBgmPlaying = false;
      }
      this.$router.go(-1);
    },
    generateReport() {
      const endTime = new Date();
      this.totalTime = (endTime - this.startTime) / 1000;

      if (this.rabbitPosition >= 100) {
        // 根据难度级别和完成时间生成不同的结果消息
        if (this.selectedDifficulty === "hard") {
          this.resultMessage = "太厉害了！你在高难度下战胜了乌龟！";
        } else if (this.selectedDifficulty === "medium") {
          this.resultMessage = "太棒了！你战胜了乌龟！";
        } else {
          this.resultMessage = "恭喜你完成比赛！继续挑战更高难度吧！";
        }
        this.resultImage = this.resultImages.win;
      } else {
        // 根据难度级别生成不同的失败消息
        if (this.selectedDifficulty === "hard") {
          this.resultMessage = "困难模式很有挑战性！再试一次，你一定能成功！";
        } else if (this.selectedDifficulty === "medium") {
          this.resultMessage = "别灰心，再试一次，相信你可以的！";
        } else {
          this.resultMessage = "继续练习，你会越来越好的！";
        }
        this.resultImage = this.resultImages.lose;
      }

      this.showReport = true;
      this.completeLevelAndSave();
    },
    formatTime(seconds) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}分${remainingSeconds}秒`;
    },
    completeLevelAndSave() {
      const completedLevels = JSON.parse(
        localStorage.getItem("completedLevels") || "[]"
      );
      if (!completedLevels.includes(this.levelId)) {
        completedLevels.push(this.levelId);
        localStorage.setItem(
          "completedLevels",
          JSON.stringify(completedLevels)
        );
      }
    },
    restartGame() {
      this.showReport = false;
      this.gameFinished = false;
      this.rabbitPosition = 0;
      this.turtlePosition = 0;
      this.correctCount = 0;
      this.totalCount = 0;
      this.accuracy = 100;
      this.startTime = new Date();
      this.generateNewLetter();

      // 保持当前难度设置
      const diffSettings = this.difficultySettings[this.selectedDifficulty];
      this.turtleSpeed = diffSettings.turtleSpeed;
      this.rabbitSpeed = diffSettings.rabbitSpeed;

      if (this.bgm && !this.isBgmPlaying) {
        this.bgm.play();
        this.isBgmPlaying = true;
      }

      this.gameInterval = setInterval(() => {
        if (!this.gameFinished) {
          this.moveTurtle();
          this.checkGameStatus();
        }
      }, 100);
    },
    closeReport() {
      this.showReport = false;
    },
    goToNextLevel() {
      this.$router.push(this.keyConfig.nextLevel);
    },
    restartCurrentGame() {
      this.gameFinished = false;
      this.rabbitPosition = 0;
      this.turtlePosition = 0;
      this.correctCount = 0;
      this.totalCount = 0;
      this.accuracy = 100;
      this.startTime = new Date();
      this.generateNewLetter();

      // 保持当前难度设置
      const diffSettings = this.difficultySettings[this.selectedDifficulty];
      this.turtleSpeed = diffSettings.turtleSpeed;
      this.rabbitSpeed = diffSettings.rabbitSpeed;

      // 清除并重新设置游戏间隔
      if (this.gameInterval) {
        clearInterval(this.gameInterval);
      }

      this.gameInterval = setInterval(() => {
        if (!this.gameFinished) {
          this.moveTurtle();
          this.checkGameStatus();
        }
      }, 100);
    },
    exitGame() {
      // 停止游戏
      this.gameStarted = false;
      this.gameFinished = false;
      if (this.gameInterval) {
        clearInterval(this.gameInterval);
      }

      // 重置所有状态
      this.rabbitPosition = 0;
      this.turtlePosition = 0;
      this.correctCount = 0;
      this.totalCount = 0;
      this.accuracy = 100;
      this.progress = 0;

      // 停止背景音乐
      if (this.bgm) {
        this.bgm.pause();
        this.isBgmPlaying = false;
      }
    },
  },
};
</script>

<style scoped>
.level-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  box-sizing: border-box; /* 确保padding不会增加总高度 */
}

.game-content {
  width: 100%;
  flex: 1; /* 允许内容区域伸展 */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.race-track {
  position: relative;
  height: 180px; /* 增加赛道高度 */
  background: linear-gradient(
    to bottom,
    #87ceeb 0%,
    #87ceeb 60%,
    #90ee90 60%,
    #90ee90 100%
  );
  border-radius: 20px;
  margin: 20px 0;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  flex: 0 0 auto; /* 不允许赛道伸缩 */
}

.track-background {
  position: absolute;
  width: 100%;
  height: 100%;
}

.track-line {
  position: absolute;
  top: 60%;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.5);
  transform: translateY(-50%);
}

.distance-markers {
  position: absolute;
  bottom: 10px;
  width: 96.5%; /* 微调宽度以更好地匹配终点线位置 */
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 600;
  left: 0;
}

.rabbit,
.turtle {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition: left 0.3s ease;
}

.character {
  font-size: 2.5em;
  position: relative;
  z-index: 2;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.shadow {
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 10px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 50%;
  filter: blur(4px);
}

.finish-line {
  position: absolute;
  right: 10px; /* 稍微调整位置 */
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 5;
}

/* 移除终点线垂直指示器 */
.finish-line::before {
  display: none; /* 隐藏红色线 */
}

.flag {
  font-size: 2.5em;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.finish-text {
  font-size: 12px;
  color: #333;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  margin-top: 5px;
  background: rgba(255, 255, 255, 0.7);
  padding: 2px 6px;
  border-radius: 10px;
  white-space: nowrap;
}

.game-display {
  text-align: center;
  margin: 20px 0;
  padding: 20px;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.game-display.correct {
  background-color: #e6ffe6;
}

.game-display.wrong {
  background-color: #ffe6e6;
}

.target-letters {
  font-size: 2em;
  margin-bottom: 10px;
}

.target-letters span {
  padding: 5px 10px;
  margin: 0 5px;
  border-radius: 5px;
}

.target-letters .current {
  background-color: #e6f3ff;
  border: 2px solid #4a90e2;
}

.target-letters .space {
  width: 80px;
  font-size: 16px;
  background: #f5f5f5;
  border-style: dashed;
}

.target-letters .space.current {
  background: #2196f3;
  border-style: solid;
  color: white;
}

.letter-instruction {
  color: #666;
  font-size: 1.2em;
}

.progress-container {
  background: white;
  padding: 20px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.progress-bar {
  height: 12px;
  background: #e0e0e0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 15px;
}

.progress {
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #2196f3);
  border-radius: 6px;
  position: relative;
  overflow: hidden;
}

.progress-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.3) 50%,
    transparent 100%
  );
  animation: progressGlow 2s linear infinite;
}

.progress-stats {
  display: flex;
  justify-content: space-between;
  gap: 20px;
}

.stat {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 15px;
  background: #f5f5f5;
  border-radius: 8px;
  font-size: 14px;
}

.stat-label {
  color: #666;
}

.stat-value {
  font-weight: bold;
  color: #2196f3;
}

@keyframes progressGlow {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

/* 添加开始游戏按钮的样式 */
.start-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.start-content {
  background: white;
  border-radius: 25px;
  padding: 40px;
  max-width: 600px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.5s ease-out;
}

.start-content h2 {
  font-size: 32px;
  color: #2196f3;
  margin: 0 0 20px 0;
}

.start-description {
  color: #666;
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 30px;
}

.start-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.start-btn,
.back-btn {
  padding: 15px 30px;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
}

.start-btn {
  background: #4caf50;
  color: white;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.start-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

.back-btn {
  background: #e9ecef;
  color: #495057;
}

.back-btn:hover {
  background: #dee2e6;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .start-content {
    background: #1a1c20;
    color: #eceff1;
  }

  .start-description {
    color: #b0bec5;
  }

  .back-btn {
    background: #34495e;
    color: #eceff1;
  }

  .back-btn:hover {
    background: #2c3e50;
  }

  .race-track {
    background: linear-gradient(
      to bottom,
      #1a237e 0%,
      #1a237e 60%,
      #1b5e20 60%,
      #1b5e20 100%
    );
  }

  .distance-markers {
    color: rgba(255, 255, 255, 0.8);
  }

  .progress-container {
    background: #2c3e50;
  }

  .progress-bar {
    background: #37474f;
  }

  .stat {
    background: #34495e;
  }

  .stat-label {
    color: #b0bec5;
  }

  .stat-value {
    color: #64b5f6;
  }

  .target-letters .space {
    background: #263238;
  }
}

/* 修改乌龟朝向 */
.turtle-char {
  transform: scaleX(-1); /* 水平翻转乌龟图标 */
  display: inline-block;
}

/* 可选：添加移动动画 */
.turtle {
  animation: turtleWalk 1s infinite alternate ease-in-out;
}

@keyframes turtleWalk {
  from {
    transform: translateY(-50%) rotate(-1deg);
  }
  to {
    transform: translateY(-50%) rotate(1deg);
  }
}

/* 添加报告弹窗样式 */
.report-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
}

.report-content {
  background: white;
  border-radius: 25px;
  max-width: 600px; /* 减小最大宽度 */
  width: 85%; /* 减小宽度百分比 */
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.5s ease-out;
}

.report-header {
  background: #4caf50;
  color: white;
  padding: 18px 25px; /* 减小内边距 */
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.report-header h2 {
  margin: 0;
  font-size: 24px; /* 减小字体大小 */
  font-weight: 600;
}

.close-button {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(255, 255, 255, 0.1);
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.report-body {
  padding: 20px; /* 减小内边距 */
}

.report-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px; /* 减小间距 */
  margin-bottom: 20px; /* 减小底部边距 */
}

.report-stats .stat-item {
  background: #f8f9fa;
  padding: 15px; /* 减小内边距 */
  border-radius: 12px;
  text-align: center;
  transition: transform 0.3s ease;
}

.report-stats .stat-item:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.report-stats .stat-icon {
  font-size: 28px; /* 减小图标大小 */
  margin-bottom: 8px;
}

.report-stats .stat-label {
  color: #666;
  font-size: 14px; /* 减小字体大小 */
  margin-bottom: 5px;
}

.report-stats .stat-value {
  font-size: 22px; /* 减小字体大小 */
  font-weight: bold;
  color: #2196f3;
}

.report-message {
  text-align: center;
  margin: 20px 0; /* 减小上下边距 */
  padding: 15px; /* 减小内边距 */
  background: #e8f5e9;
  border-radius: 12px;
}

.report-message img {
  width: 100px; /* 减小图片大小 */
  height: 100px;
  object-fit: contain;
  margin-bottom: 15px;
}

.message-text {
  font-size: 18px; /* 减小字体大小 */
  color: #2e7d32;
  font-weight: 500;
}

.report-footer {
  padding: 18px; /* 减小内边距 */
  background: #f8f9fa;
  display: flex;
  justify-content: center;
  gap: 15px; /* 减小间距 */
}

.report-footer button {
  padding: 12px 25px; /* 减小内边距 */
  border: none;
  border-radius: 10px;
  font-size: 16px; /* 减小字体大小 */
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.restart-btn {
  background: #ff9800;
  color: white;
  box-shadow: 0 4px 15px rgba(255, 152, 0, 0.3);
}

.next-btn {
  background: #4caf50;
  color: white;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);
}

.report-footer button:hover {
  transform: translateY(-2px);
}

.restart-btn:hover {
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.4);
}

.next-btn:hover {
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .report-content {
    background: #1a1c20;
    color: #eceff1;
  }

  .report-stats .stat-item {
    background: #2c3e50;
  }

  .report-stats .stat-label {
    color: #b0bec5;
  }

  .report-stats .stat-value {
    color: #64b5f6;
  }

  .report-message {
    background: #1b5e20;
  }

  .message-text {
    color: #81c784;
  }

  .report-footer {
    background: #263238;
  }
}

/* 响应式调整 */
@media (min-height: 800px) {
  .race-track {
    height: 220px; /* 在更高的屏幕上进一步增加赛道高度 */
  }

  .character {
    font-size: 3em; /* 增大角色大小 */
  }
}

@media (max-width: 768px) {
  .level-container {
    padding: 10px; /* 在小屏幕上减少内边距 */
  }

  .game-content {
    max-width: 100%; /* 在小屏幕上占满宽度 */
  }
}

/* 添加控制按钮样式 */
.control-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.control-btn {
  padding: 12px 24px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.control-btn.restart {
  background: #ff9800;
  color: white;
  box-shadow: 0 4px 15px rgba(255, 152, 0, 0.2);
}

.control-btn.exit {
  background: #e9ecef;
  color: #495057;
}

.control-btn:hover {
  transform: translateY(-2px);
}

.control-btn.restart:hover {
  background: #f57c00;
  box-shadow: 0 6px 20px rgba(255, 152, 0, 0.3);
}

.control-btn.exit:hover {
  background: #dee2e6;
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .control-buttons {
    border-top-color: rgba(255, 255, 255, 0.1);
  }

  .control-btn.exit {
    background: #34495e;
    color: #eceff1;
  }

  .control-btn.exit:hover {
    background: #2c3e50;
  }
}

/* 添加键位高亮样式 */
.key-highlight {
  display: inline-block;
  background-color: #e6f3ff;
  color: #2196f3;
  border: 1px solid #4a90e2;
  border-radius: 4px;
  padding: 2px 6px;
  margin: 0 2px;
  font-weight: bold;
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .key-highlight {
    background-color: #1a237e;
    color: #64b5f6;
    border-color: #1976d2;
  }
}

/* 添加难度选择样式 */
.difficulty-selection {
  margin: 20px 0;
  text-align: center;
}

.difficulty-selection h3 {
  font-size: 20px;
  color: #555;
  margin-bottom: 15px;
}

.difficulty-options {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}

.difficulty-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background: #f8f9fa;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 120px;
}

.difficulty-btn.active {
  border-color: #4caf50;
  background: #e8f5e9;
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(76, 175, 80, 0.2);
}

.difficulty-icon {
  font-size: 28px;
  margin-bottom: 8px;
}

.difficulty-name {
  font-weight: 600;
  font-size: 16px;
  color: #333;
  margin-bottom: 5px;
}

.difficulty-desc {
  font-size: 12px;
  color: #777;
}

.difficulty-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .difficulty-selection h3 {
    color: #b0bec5;
  }

  .difficulty-btn {
    background: #263238;
    border-color: #37474f;
  }

  .difficulty-btn.active {
    border-color: #4caf50;
    background: #1b5e20;
  }

  .difficulty-name {
    color: #eceff1;
  }

  .difficulty-desc {
    color: #b0bec5;
  }
}

/* 添加游戏中难度显示样式 */
.difficulty-display {
  text-align: center;
  margin: 15px 0;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 8px;
  font-size: 16px;
}

.difficulty-label {
  font-weight: 500;
  color: #666;
  margin-right: 10px;
}

.difficulty-value {
  font-weight: 600;
}

.difficulty-easy {
  color: #4caf50;
}

.difficulty-medium {
  color: #2196f3;
}

.difficulty-hard {
  color: #f44336;
}

/* 报告中的难度统计项样式 */
.difficulty-stat {
  grid-column: span 2;
  background: #f0f8ff !important;
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .difficulty-display {
    background: #263238;
  }

  .difficulty-label {
    color: #b0bec5;
  }

  .difficulty-stat {
    background: #1a237e !important;
  }
}

/* 添加难度提示样式 */
.difficulty-tip {
  margin-top: 10px;
  font-size: 14px;
  color: #5c6bc0;
  font-style: italic;
  background: rgba(92, 107, 192, 0.1);
  padding: 8px 12px;
  border-radius: 8px;
}

/* 深色模式支持 */
@media (prefers-color-scheme: dark) {
  .difficulty-tip {
    color: #7986cb;
    background: rgba(121, 134, 203, 0.2);
  }
}
</style>
